import React from 'react';
import { ShieldCheck, ShieldAlert, Shield } from 'lucide-react';

/**
 * VerificationBadge — small visual indicator of a listing's verification state.
 *
 * Status values (matching what the backend attaches to properties):
 *   "verified"   — host emailVerified + host_profile complete + not suspended
 *   "pending"    — host has the role but onboarding is incomplete
 *   "unverified" — no host record, missing role, or suspended (default)
 *
 * Usage:
 *   <VerificationBadge status={listing.verificationStatus} />
 *   <VerificationBadge status="verified" size="lg" />
 */
const VerificationBadge = ({ status = 'unverified', size = 'md', showLabel = true, className = '' }) => {
  const config = {
    verified: {
      icon: ShieldCheck,
      label: 'Identity verified',
      classes: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      iconClass: 'text-emerald-600',
    },
    pending: {
      icon: Shield,
      label: 'Verification pending',
      classes: 'bg-amber-50 text-amber-700 border border-amber-200',
      iconClass: 'text-amber-600',
    },
    unverified: {
      icon: ShieldAlert,
      label: 'Unverified',
      classes: 'bg-gray-50 text-gray-600 border border-gray-200',
      iconClass: 'text-gray-500',
    },
  };

  const c = config[status] || config.unverified;
  const Icon = c.icon;

  const sizeClasses = {
    sm: 'text-[10px] px-1.5 py-0.5 gap-1',
    md: 'text-xs px-2 py-1 gap-1.5',
    lg: 'text-sm px-3 py-1.5 gap-2',
  };
  const iconSize = { sm: 10, md: 12, lg: 14 };

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${c.classes} ${sizeClasses[size]} ${className}`}
      data-testid={`verification-badge-${status}`}
      title={c.label}
    >
      <Icon size={iconSize[size]} className={c.iconClass} />
      {showLabel && <span>{c.label}</span>}
    </span>
  );
};

export default VerificationBadge;
